/**
 * Product Title Scroll Animation
 * Detects overflow and applies smooth scrolling animation on hover
 */

(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    scrollSpeed: 8, // pixels per second (slower for smoother effect)
    minScrollDistance: 10, // minimum pixels to trigger scroll
    checkDelay: 100, // delay before checking overflow (ms)
    additionalScroll: 20 // extra pixels to scroll past the end
  };

  /**
   * Calculate if text overflows and by how much
   */
  function calculateOverflow(container, scrollElement) {
    const containerWidth = container.offsetWidth;
    const scrollWidth = scrollElement.scrollWidth;
    
    return {
      hasOverflow: scrollWidth > containerWidth,
      overflowAmount: Math.max(0, scrollWidth - containerWidth)
    };
  }

  /**
   * Set up scroll animation for a single product title
   */
  function setupTitleScroll(titleElement) {
    const container = titleElement.closest('.product-title-container');
    const textWrapper = titleElement.closest('.product-title-text');
    
    if (!container) return;
    // Check for overflow
    const { hasOverflow, overflowAmount } = calculateOverflow(container, titleElement);
    
    if (hasOverflow && overflowAmount > CONFIG.minScrollDistance) {
      // Mark as scrollable
      titleElement.classList.add('is-scrollable');
      if (textWrapper) {
        textWrapper.classList.add('has-overflow');
      }
      
      // Calculate scroll distance and duration
      const scrollDistance = overflowAmount + CONFIG.additionalScroll;
      const duration = Math.max(3, scrollDistance / CONFIG.scrollSpeed);
      
      // Set CSS custom properties for animation
      titleElement.style.setProperty('--scroll-distance', `-${scrollDistance}px`);
      titleElement.style.setProperty('--scroll-duration', `${duration}s`);
      
      // Add title attribute for accessibility
      if (!titleElement.hasAttribute('title')) {
        titleElement.setAttribute('title', titleElement.textContent.trim());
      }
    } else {
      // Remove scrollable class if not needed
      titleElement.classList.remove('is-scrollable');
      if (textWrapper) {
        textWrapper.classList.remove('has-overflow');
      }
    }
  }

  /**
   * Initialize all product titles on the page
   */
  function initializeAllTitles() {
    const titles = document.querySelectorAll('.product-title-scroll');
    titles.forEach(setupTitleScroll);
  }

  /**
   * Handle dynamic content (AJAX loaded products)
   */
  function handleDynamicContent() {
    // Set up MutationObserver for dynamically added content
    const observer = new MutationObserver((mutations) => {
      let hasNewTitles = false;
      
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) { // Element node
              if (node.classList && node.classList.contains('product-title-scroll')) {
                hasNewTitles = true;
              } else if (node.querySelectorAll) {
                const titles = node.querySelectorAll('.product-title-scroll');
                if (titles.length > 0) hasNewTitles = true;
              }
            }
          });
        }
      });
      
      if (hasNewTitles) {
        // Debounce initialization
        clearTimeout(handleDynamicContent.timeout);
        handleDynamicContent.timeout = setTimeout(initializeAllTitles, CONFIG.checkDelay);
      }
    });

    // Start observing
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Handle window resize
   */
  function handleResize() {
    clearTimeout(handleResize.timeout);
    handleResize.timeout = setTimeout(initializeAllTitles, 200);
  }

  /**
   * Initialize when DOM is ready
   */
  function init() {
    // Initial setup
    setTimeout(initializeAllTitles, CONFIG.checkDelay);
    
    // Set up dynamic content handling
    handleDynamicContent();
    
    // Handle window resize
    window.addEventListener('resize', handleResize);
    
    // Handle Shopify theme editor
    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:section:load', initializeAllTitles);
      document.addEventListener('shopify:section:reorder', initializeAllTitles);
    }
  }

  // Initialize based on DOM state
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose API for manual refresh if needed
  window.ProductTitleScroll = {
    refresh: initializeAllTitles,
    setup: setupTitleScroll
  };

})();

